import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  useGetImportRunQuery,
  useArchiveContactMutation,
  useRestoreContactMutation,
  useDeleteContactPermanentlyMutation,
  useGetContactsQuery,
  useGetContactListsQuery,
  useGetTagsQuery,
  useBulkAddTagMutation,
  useGetLeadSummaryQuery,
  useGetDealStagesQuery,
  usePromoteContactMutation,
} from "@/store/rest_api";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import DealStageChip from "@/components/deals/DealStageChip";
import { DealStageCategory, stageCategoryOf } from "@/enum/DealStageCategory";
import { useConversionToast } from "@/hooks/useConversionToast";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { getContactDisplayName } from "@/util/string";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  MoreVertical,
  Plus,
  Pencil,
  ScanSearch,
  Trash2,
  Archive,
  ArchiveRestore,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  Loader2,
  Phone,
  EyeIcon,
  Upload,
  Download,
  ArrowUpDown,
  Filter,
  X,
  TagIcon,
} from "lucide-react";
import { toast } from "sonner";
import SkipTraceDialog from "@/components/skip-trace/SkipTraceDialog";
import { SkipTraceScope } from "@/enum/SkipTraceScope";
import type Contact from "@/model/interface/Contact";
import type ContactList from "@/model/interface/ContactList";
import { ContactType } from "@/enum/ContactType";
import ImportWizard from "@/components/imports/ImportWizard";
import ExportDialog from "@/components/exports/ExportDialog";
import { useCanExport } from "@/hooks/useCanExport";
import type { ExportRequest } from "@/model/interface/DataExport";
import { apiErrorMessage } from "@/util/apiError";
import { useBreadcrumbs } from "@/components/provider/BreadcrumbsProvider";

type SortDir = "asc" | "desc";
type SortState = { field: string; dir: SortDir };

function useDebouncedValue<T>(value: T, delayMs: number) {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setDebounced(value), delayMs);
    return () => clearTimeout(t);
  }, [value, delayMs]);
  return debounced;
}

function getContactListNames(contact: Contact) {
  return (contact.contactListLinks?.map((l) => l.contactList?.name).filter(Boolean) as string[] | undefined) ?? [];
}

type HasPhoneFilter = "all" | "yes" | "no";

const ALL_LEADS_TAB = "all";
const NO_DEAL_TAB = "none";

interface ContactListPageProps {
  lockedType?: ContactType;
  excludeType?: ContactType;
  heading?: string;
  getDetailPath?: (id: string) => string;
}

export default function ContactListPage({
  lockedType,
  excludeType,
  heading = "Contacts",
  getDetailPath = (contactId: string) => `/contacts/${contactId}/detail`,
}: ContactListPageProps = {}) {
  const navigate = useNavigate();
  const { setSection, setSectionPath, setPage: setBreadcrumbPage } = useBreadcrumbs();
  const [archiveContact] = useArchiveContactMutation();
  const [restoreContact] = useRestoreContactMutation();
  const [deleteContactPermanently] = useDeleteContactPermanentlyMutation();
  const [promoteContact] = usePromoteContactMutation();
  const showConversionToast = useConversionToast();
  const [promotingContactId, setPromotingContactId] = useState<string | null>(null);

  const isLeadsPage = lockedType === ContactType.LEAD;
  const sectionPath = isLeadsPage ? "/leads" : "/contacts";

  useEffect(() => {
    setSection(heading);
    setSectionPath(sectionPath);
    setBreadcrumbPage("");
  }, [heading, sectionPath, setSection, setSectionPath, setBreadcrumbPage]);
  const [bulkAddTag] = useBulkAddTagMutation();
  const [contactToDelete, setContactToDelete] = useState<Contact | null>(null);
  const [contactToTrace, setContactToTrace] = useState<Contact | null>(null);
  const [traceSelectionOpen, setTraceSelectionOpen] = useState(false);
  const [bulkDeleteOpen, setBulkDeleteOpen] = useState(false);
  const [showArchived, setShowArchived] = useState(false);
  const [contactToPurge, setContactToPurge] = useState<Contact | null>(null);
  const [importOpen, setImportOpen] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();
  const importRunId = searchParams.get("importRunId") ?? undefined;
  const importAction = searchParams.get("importAction") ?? undefined;
  const { data: importRun } = useGetImportRunQuery(importRunId ?? "", { skip: !importRunId });
  const [selectAllMatching, setSelectAllMatching] = useState(false);
  const [exportOpen, setExportOpen] = useState(false);
  const { canExport } = useCanExport();
  const { data: leadSummary } = useGetLeadSummaryQuery(undefined, { skip: !isLeadsPage });
  const { data: dealStages = [] } = useGetDealStagesQuery({}, { skip: !isLeadsPage });
  const leadStages = dealStages.filter((stage) => stageCategoryOf(stage) === DealStageCategory.LEAD);
  const stageTab = isLeadsPage && !showArchived ? (searchParams.get("stage") ?? ALL_LEADS_TAB) : ALL_LEADS_TAB;
  const dealStageIdParam = stageTab !== ALL_LEADS_TAB && stageTab !== NO_DEAL_TAB ? stageTab : undefined;
  const withoutDealParam = stageTab === NO_DEAL_TAB ? true : undefined;

  const [page, setPage] = useState(0);
  const [size, setSize] = useState(20);

  const [search, setSearch] = useState("");
  const debouncedSearch = useDebouncedValue(search.trim(), 300);

  const [company, setCompany] = useState("");
  const debouncedCompany = useDebouncedValue(company.trim(), 300);

  const [hasPhone, setHasPhone] = useState<HasPhoneFilter>("all");
  const [selectedListIds, setSelectedListIds] = useState<Set<string>>(new Set());
  const [selectedTagIds, setSelectedTagIds] = useState<Set<string>>(new Set());

  const [sort, setSort] = useState<SortState>({ field: "createdAt", dir: "desc" });
  const sortParam = useMemo(() => `${sort.field},${sort.dir}`, [sort]);

  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());

  const { data: listsResp } = useGetContactListsQuery(
    { page: 0, size: 200, sort: "createdAt,desc" },
    { refetchOnMountOrArgChange: false },
  );

  const allLists: ContactList[] = (listsResp?.items ?? []) as any;

  const { data: allTags = [] } = useGetTagsQuery({});

  const listIdsParam = useMemo(() => {
    const ids = Array.from(selectedListIds);
    ids.sort();
    return ids.length ? ids : undefined;
  }, [selectedListIds]);

  const tagIdsParam = useMemo(() => {
    const ids = Array.from(selectedTagIds);
    ids.sort();
    return ids.length ? ids : undefined;
  }, [selectedTagIds]);

  const hasPhoneParam = useMemo(() => {
    if (hasPhone === "all") return undefined;
    return hasPhone === "yes";
  }, [hasPhone]);

  const { data, isLoading, error, isFetching } = useGetContactsQuery({
    page,
    size,
    sort: sortParam,
    q: debouncedSearch || undefined,
    company: debouncedCompany || undefined,
    hasPhone: hasPhoneParam,
    listIds: listIdsParam,
    tagIds: tagIdsParam,
    type: lockedType,
    excludeType,
    archived: showArchived,
    importRunId,
    importAction,
    dealStageId: dealStageIdParam,
    withoutDeal: withoutDealParam,
  });

  const contacts = data?.items ?? [];
  const totalPages = data?.totalPages ?? 0;
  const totalElements = data?.totalElements ?? 0;
  const isLast = data?.last ?? true;

  const selectedCount = selectedIds.size;

  const allOnPageIds = useMemo(
    () => contacts.map((contact: Contact) => contact.id).filter(Boolean) as string[],
    [contacts],
  );

  const allOnPageSelected = useMemo(() => {
    if (allOnPageIds.length === 0) return false;
    return allOnPageIds.every((id) => selectedIds.has(id));
  }, [allOnPageIds, selectedIds]);

  const someOnPageSelected = useMemo(() => {
    if (allOnPageIds.length === 0) {
      return false;
    }
    const any = allOnPageIds.some((id) => selectedIds.has(id));

    return any && !allOnPageSelected;
  }, [allOnPageIds, selectedIds, allOnPageSelected]);

  const toggleSort = (field: string) => {
    setSort((prev) => {
      if (prev.field !== field) {
        return { field, dir: "asc" };
      }

      return { field, dir: prev.dir === "asc" ? "desc" : "asc" };
    });
  };

  const isSortedBy = (field: string) => sort.field === field;

  const handleDelete = async () => {
    if (!contactToDelete) {
      return;
    }
    try {
      await archiveContact(contactToDelete.id).unwrap();
      toast.success("Contact archived");
      setContactToDelete(null);

      setSelectedIds((prev) => {
        const next = new Set(prev);
        next.delete(contactToDelete.id);

        return next;
      });
    } catch (error) {
      toast.error(apiErrorMessage(error, "Failed to archive contact"));
    }
  };

  const handleRestore = async (contact: Contact) => {
    try {
      await restoreContact(contact.id).unwrap();
      toast.success("Contact restored");
      setSelectedIds((prev) => {
        const next = new Set(prev);
        next.delete(contact.id);
        return next;
      });
    } catch (error) {
      toast.error(apiErrorMessage(error, "Failed to restore contact"));
    }
  };

  const handlePurge = async () => {
    if (!contactToPurge) {
      return;
    }
    try {
      await deleteContactPermanently(contactToPurge.id).unwrap();
      toast.success("Contact deleted permanently");
      setContactToPurge(null);
      setSelectedIds((prev) => {
        const next = new Set(prev);
        next.delete(contactToPurge.id);
        return next;
      });
    } catch (error) {
      toast.error(apiErrorMessage(error, "Failed to delete contact"));
    }
  };

  const handleBulkRestore = async () => {
    if (selectedIds.size === 0) {
      return;
    }
    const ids = Array.from(selectedIds);
    let ok = 0;
    let fail = 0;
    for (const id of ids) {
      try {
        await restoreContact(id).unwrap();
        ok++;
      } catch {
        fail++;
      }
    }
    setSelectedIds(new Set());
    if (ok > 0) {
      toast.success(`Restored ${ok} contact${ok === 1 ? "" : "s"}`);
    }
    if (fail > 0) {
      toast.error(`Failed to restore ${fail} contact${fail === 1 ? "" : "s"}`);
    }
  };

  const handleBulkDelete = async () => {
    if (selectedIds.size === 0) {
      return;
    }

    const ids = Array.from(selectedIds);

    try {
      let ok = 0;
      let fail = 0;

      for (const id of ids) {
        try {
          await archiveContact(id).unwrap();
          ok++;
        } catch {
          fail++;
        }
      }

      setSelectedIds(new Set());
      setBulkDeleteOpen(false);

      if (ok > 0) {
        toast.success(`Archived ${ok} contact${ok === 1 ? "" : "s"}`);
      }
      if (fail > 0) {
        toast.error(`Failed to archive ${fail} contact${fail === 1 ? "" : "s"}`);
      }
    } catch (error) {
      toast.error(apiErrorMessage(error, "Bulk archive failed"));
    }
  };

  const handleCloseImport = () => setImportOpen(false);

  const setStageTab = (nextTab: string) => {
    const nextParams = new URLSearchParams(searchParams);
    if (nextTab === ALL_LEADS_TAB) {
      nextParams.delete("stage");
    } else {
      nextParams.set("stage", nextTab);
    }
    setSearchParams(nextParams);
  };

  const handlePromote = async (contact: Contact) => {
    setPromotingContactId(contact.id);
    try {
      const deal = await promoteContact(contact.id).unwrap();
      if (deal.convertedContactId) {
        showConversionToast(deal.convertedContactId, getContactDisplayName(contact));
      } else {
        toast.success(`Added to ${deal.stage?.name ?? "the pipeline"}`);
      }
    } catch (error) {
      toast.error(apiErrorMessage(error, "Could not promote the lead"));
    } finally {
      setPromotingContactId(null);
    }
  };

  const clearImportFilter = () => {
    const nextParams = new URLSearchParams(searchParams);
    nextParams.delete("importRunId");
    nextParams.delete("importAction");
    setSearchParams(nextParams);
  };

  const toggleSelectOne = (id: string, checked: boolean) => {
    setSelectAllMatching(false);
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (checked) {
        next.add(id);
      } else {
        next.delete(id);
      }

      return next;
    });
  };

  const toggleSelectAllOnPage = (checked: boolean) => {
    setSelectAllMatching(false);
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (checked) {
        allOnPageIds.forEach((id) => next.add(id));
      } else {
        allOnPageIds.forEach((id) => next.delete(id));
      }
      return next;
    });
  };

  const toggleListFilter = (listId: string, checked: boolean) => {
    setSelectedListIds((prev) => {
      const next = new Set(prev);
      if (checked) next.add(listId);
      else next.delete(listId);
      return next;
    });
  };

  const toggleTagFilter = (tagId: string, checked: boolean) => {
    setSelectedTagIds((prev) => {
      const next = new Set(prev);
      if (checked) next.add(tagId);
      else next.delete(tagId);
      return next;
    });
  };

  const clearFilters = () => {
    setCompany("");
    setHasPhone("all");
    setSelectedListIds(new Set());
    setSelectedTagIds(new Set());
  };

  const filtersActive =
    debouncedCompany.length > 0 || hasPhone !== "all" || selectedListIds.size > 0 || selectedTagIds.size > 0;

  useEffect(() => {
    setPage(0);
    setSelectedIds(new Set());
    setSelectAllMatching(false);
  }, [debouncedSearch, size, sortParam, debouncedCompany, hasPhoneParam, listIdsParam, tagIdsParam, showArchived, importRunId, importAction, stageTab]);

  if (isLoading) {
    return <div className="p-6">Loading...</div>;
  }

  if (error) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-destructive">Failed to load contacts</div>
      </div>
    );
  }

  const tableTitle = showArchived ? `Archived ${heading}` : `All ${heading}`;
  const singularName = isLeadsPage ? "lead" : "contact";
  const createPath = isLeadsPage ? `/contacts/new?type=${ContactType.LEAD}` : "/contacts/new";
  const createLabel = isLeadsPage ? "Create Lead" : "Create Contact";
  const viewNarrowed =
    debouncedSearch.length > 0 || filtersActive || Boolean(importRunId) || stageTab !== ALL_LEADS_TAB;
  const exportCount = selectAllMatching ? totalElements : selectedCount;
  const exportRequest: ExportRequest = {
    kind: "CONTACTS",
    contactIds: selectAllMatching ? undefined : Array.from(selectedIds),
    contactFilter: {
      q: debouncedSearch || undefined,
      company: debouncedCompany || undefined,
      hasPhone: hasPhoneParam,
      listIds: listIdsParam,
      tagIds: tagIdsParam,
      type: lockedType,
      excludeType,
      archived: showArchived,
      importRunId,
      importAction,
      dealStageId: dealStageIdParam,
      withoutDeal: withoutDealParam,
    },
  };

  return (
    <div className="space-y-6 p-4">
      <div className="flex justify-between items-center gap-3">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">{heading}</h1>
          <p className="text-muted-foreground tabular-nums">
            {isLeadsPage && leadSummary
              ? `${leadSummary.total.toLocaleString()} ${leadSummary.total === 1 ? "lead" : "leads"}, ${leadSummary.addedToday.toLocaleString()} added today`
              : "Search by name, company, phone or email"}
          </p>
        </div>

        <div className="flex items-center gap-2">
          {selectedCount > 0 && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline">
                  <span className="tabular-nums">{(selectAllMatching ? totalElements : selectedCount).toLocaleString()}</span>
                  &nbsp;selected
                  <ChevronDown className="ml-2 h-4 w-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                {canExport && (
                  <DropdownMenuItem onClick={() => setExportOpen(true)}>
                    <Download className="mr-2 h-4 w-4" />
                    Export
                  </DropdownMenuItem>
                )}
                <DropdownMenuItem onClick={() => setTraceSelectionOpen(true)} disabled={selectAllMatching}>
                  <ScanSearch className="mr-2 h-4 w-4" />
                  Skip Trace
                </DropdownMenuItem>
                <DropdownMenuSub>
                  <DropdownMenuSubTrigger disabled={selectAllMatching}>
                    <TagIcon className="mr-2 h-4 w-4" />
                    Add Tag
                  </DropdownMenuSubTrigger>
                  <DropdownMenuSubContent className="w-56 max-h-80 overflow-auto">
                    {allTags.length === 0 && (
                      <div className="px-2 py-2 text-sm text-muted-foreground">No tags available</div>
                    )}
                    {allTags.map((t) => (
                      <DropdownMenuItem
                        key={t.id}
                        onClick={async () => {
                          try {
                            const res = await bulkAddTag({
                              tagId: t.id,
                              contactIds: Array.from(selectedIds),
                            }).unwrap();
                            toast.success(`Tagged ${res.added} contact${res.added === 1 ? "" : "s"}`);
                          } catch {
                            toast.error("Failed to tag contacts");
                          }
                        }}
                        className="flex items-center gap-2"
                      >
                        <span className="h-3 w-3 rounded-full shrink-0" style={{ backgroundColor: t.color }} />
                        {t.name}
                      </DropdownMenuItem>
                    ))}
                  </DropdownMenuSubContent>
                </DropdownMenuSub>
                <DropdownMenuSeparator />
                {showArchived ? (
                  <DropdownMenuItem onClick={() => void handleBulkRestore()} disabled={selectAllMatching}>
                    <ArchiveRestore className="mr-2 h-4 w-4" />
                    Restore
                  </DropdownMenuItem>
                ) : (
                  <DropdownMenuItem onClick={() => setBulkDeleteOpen(true)} className="text-destructive" disabled={selectAllMatching}>
                    <Trash2 className="mr-2 h-4 w-4" />
                    Archive
                  </DropdownMenuItem>
                )}
              </DropdownMenuContent>
            </DropdownMenu>
          )}

          <Button variant="outline" onClick={() => setImportOpen(true)}>
            <Upload className="mr-2 h-4 w-4" />
            Import
          </Button>

          <Button onClick={() => navigate(createPath)}>
            <Plus className="mr-2 h-4 w-4" />
            {createLabel}
          </Button>
        </div>
      </div>

      <div className="flex flex-col gap-3">
        <div className="flex flex-col md:flex-row md:items-center gap-3">
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by name, phone or email..."
            className="w-full md:w-80"
          />
          <Button
            variant={showArchived ? "secondary" : "outline"}
            size="sm"
            onClick={() => setShowArchived((value) => !value)}
            title={showArchived ? "Back to active contacts" : "Show archived contacts"}
          >
            <Archive className="mr-2 h-4 w-4" />
            Archived
          </Button>
          {importRunId && (
            <span className="status-pill-info">
              {importAction === "UPDATED" ? "Updated by" : "Added by"} {importRun?.fileName ?? "an import"}
              <button type="button" onClick={clearImportFilter} title="Show everyone" className="ml-0.5">
                <X className="h-3 w-3" />
              </button>
            </span>
          )}
          {isFetching && <div className="text-xs text-muted-foreground">Refreshing…</div>}
        </div>

        <div className="flex flex-col md:flex-row md:items-center gap-3">
          <div className="flex items-center gap-2">
            <Filter className="h-4 w-4 text-muted-foreground" />
            <span className="text-sm text-muted-foreground">Filters</span>
          </div>

          <div className="flex flex-col md:flex-row md:items-center gap-3 flex-1">
            <Input
              value={company}
              onChange={(e) => setCompany(e.target.value)}
              placeholder="Company…"
              className="w-full md:w-64"
            />

            <Select value={hasPhone} onValueChange={(v) => setHasPhone(v as HasPhoneFilter)}>
              <SelectTrigger className="w-full md:w-44">
                <SelectValue placeholder="Has phone" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Has phone: All</SelectItem>
                <SelectItem value="yes">Has phone: Yes</SelectItem>
                <SelectItem value="no">Has phone: No</SelectItem>
              </SelectContent>
            </Select>

            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" className="w-full md:w-auto justify-between">
                  Lists
                  {selectedListIds.size > 0 && (
                    <Badge variant="secondary" className="ml-2">
                      {selectedListIds.size}
                    </Badge>
                  )}
                </Button>
              </DropdownMenuTrigger>

              <DropdownMenuContent align="start" className="w-72 max-h-80 overflow-auto">
                {allLists.length === 0 && <div className="px-2 py-2 text-sm text-muted-foreground">No lists</div>}

                {allLists.map((l) => (
                  <DropdownMenuItem key={l.id} onSelect={(e) => e.preventDefault()} className="flex items-center gap-2">
                    <Checkbox
                      checked={l.id ? selectedListIds.has(l.id) : false}
                      onCheckedChange={(v) => l.id && toggleListFilter(l.id, v === true)}
                    />
                    <span className="truncate">{l.name ?? "Unnamed list"}</span>
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>

            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" className="w-full md:w-auto justify-between">
                  Tags
                  {selectedTagIds.size > 0 && (
                    <Badge variant="secondary" className="ml-2">
                      {selectedTagIds.size}
                    </Badge>
                  )}
                </Button>
              </DropdownMenuTrigger>

              <DropdownMenuContent align="start" className="w-72 max-h-80 overflow-auto">
                {allTags.length === 0 && <div className="px-2 py-2 text-sm text-muted-foreground">No tags</div>}

                {allTags.map((t) => (
                  <DropdownMenuItem key={t.id} onSelect={(e) => e.preventDefault()} className="flex items-center gap-2">
                    <Checkbox
                      checked={selectedTagIds.has(t.id)}
                      onCheckedChange={(v) => toggleTagFilter(t.id, v === true)}
                    />
                    <span className="h-3 w-3 rounded-full shrink-0" style={{ backgroundColor: t.color }} />
                    <span className="truncate">{t.name}</span>
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>

            <div className="flex-1" />

            {filtersActive && (
              <Button variant="ghost" onClick={clearFilters} className="w-full md:w-auto">
                <X className="mr-2 h-4 w-4" />
                Clear filters
              </Button>
            )}
          </div>
        </div>

        {filtersActive && (
          <div className="flex flex-wrap gap-2">
            {debouncedCompany && <Badge variant="outline">Company: {debouncedCompany}</Badge>}
            {hasPhone !== "all" && <Badge variant="outline">Has phone: {hasPhone === "yes" ? "Yes" : "No"}</Badge>}
            {Array.from(selectedListIds)
              .slice(0, 4)
              .map((id) => {
                const list = allLists.find((l) => l.id === id);
                return (
                  <Badge key={id} variant="outline">
                    List: {list?.name ?? id}
                  </Badge>
                );
              })}
            {selectedListIds.size > 4 && <Badge variant="outline">+{selectedListIds.size - 4} more lists</Badge>}
            {Array.from(selectedTagIds)
              .slice(0, 4)
              .map((id) => {
                const tag = allTags.find((t) => t.id === id);
                return (
                  <Badge key={id} variant="outline">
                    Tag: {tag?.name ?? id}
                  </Badge>
                );
              })}
            {selectedTagIds.size > 4 && <Badge variant="outline">+{selectedTagIds.size - 4} more tags</Badge>}
          </div>
        )}
      </div>

      {isLeadsPage && !showArchived && (
        <Tabs value={stageTab} onValueChange={setStageTab}>
          <TabsList className="h-auto flex-wrap">
            <TabsTrigger value={ALL_LEADS_TAB} className="gap-2">
              All
              <span className="tabular-nums text-muted-foreground">{leadSummary?.total ?? 0}</span>
            </TabsTrigger>
            {leadStages.map((stage) => (
              <TabsTrigger key={stage.id} value={stage.id} className="gap-2">
                <span className="h-2 w-2 rounded-full shrink-0" style={{ backgroundColor: stage.color }} />
                {stage.name}
                <span className="tabular-nums text-muted-foreground">{leadSummary?.stageCounts[stage.id] ?? 0}</span>
              </TabsTrigger>
            ))}
            <TabsTrigger value={NO_DEAL_TAB} className="gap-2">
              No deal yet
              <span className="tabular-nums text-muted-foreground">{leadSummary?.withoutDeal ?? 0}</span>
            </TabsTrigger>
          </TabsList>
        </Tabs>
      )}

      <Card>
        <CardHeader>
          <CardTitle>{tableTitle}</CardTitle>
        </CardHeader>

        <CardContent className="space-y-4">
          {allOnPageSelected && totalElements > allOnPageIds.length && (
            <div className="flex flex-wrap items-center gap-2 text-sm text-muted-foreground">
              {selectAllMatching ? (
                <>
                  <span>
                    All <span className="tabular-nums">{totalElements.toLocaleString()}</span> matching{" "}
                    {heading.toLowerCase()} are selected.
                  </span>
                  <Button
                    variant="link"
                    className="h-auto p-0"
                    onClick={() => {
                      setSelectAllMatching(false);
                      setSelectedIds(new Set());
                    }}
                  >
                    Clear selection
                  </Button>
                </>
              ) : (
                <>
                  <span>
                    All <span className="tabular-nums">{allOnPageIds.length}</span> on this page are selected.
                  </span>
                  <Button variant="link" className="h-auto p-0" onClick={() => setSelectAllMatching(true)}>
                    Select all {totalElements.toLocaleString()} matching
                  </Button>
                </>
              )}
            </div>
          )}
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-10">
                  <Checkbox
                    checked={allOnPageSelected ? true : someOnPageSelected ? "indeterminate" : false}
                    onCheckedChange={(v) => toggleSelectAllOnPage(v === true)}
                    aria-label="Select all contacts on this page"
                  />
                </TableHead>

                <TableHead className="cursor-pointer select-none" onClick={() => toggleSort("firstName")}>
                  <div className="flex items-center gap-2">
                    Name{" "}
                    <ArrowUpDown className={isSortedBy("firstName") ? "h-4 w-4" : "h-4 w-4 text-muted-foreground"} />
                  </div>
                </TableHead>

                <TableHead className="cursor-pointer select-none" onClick={() => toggleSort("company")}>
                  <div className="flex items-center gap-2">
                    Company{" "}
                    <ArrowUpDown className={isSortedBy("company") ? "h-4 w-4" : "h-4 w-4 text-muted-foreground"} />
                  </div>
                </TableHead>

                <TableHead>Phones</TableHead>
                <TableHead>Lists</TableHead>
                <TableHead>Tags</TableHead>
                <TableHead>Stage</TableHead>

                <TableHead className="cursor-pointer select-none" onClick={() => toggleSort("type")}>
                  <div className="flex items-center gap-2">
                    Type <ArrowUpDown className={isSortedBy("type") ? "h-4 w-4" : "h-4 w-4 text-muted-foreground"} />
                  </div>
                </TableHead>

                <TableHead className="cursor-pointer select-none" onClick={() => toggleSort("createdAt")}>
                  <div className="flex items-center gap-2">
                    Created{" "}
                    <ArrowUpDown className={isSortedBy("createdAt") ? "h-4 w-4" : "h-4 w-4 text-muted-foreground"} />
                  </div>
                </TableHead>

                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>

            <TableBody className={isFetching ? "pointer-events-none opacity-50" : undefined}>
              {contacts.map((contact: Contact) => {
                const listNames = getContactListNames(contact);
                const checked = contact.id ? selectedIds.has(contact.id) : false;

                return (
                  <TableRow key={contact.id}>
                    <TableCell className="align-middle">
                      <Checkbox
                        checked={checked}
                        onCheckedChange={(v) => contact.id && toggleSelectOne(contact.id, v === true)}
                        aria-label={`Select contact ${getContactDisplayName(contact) || contact.id}`}
                      />
                    </TableCell>

                    <TableCell>
                      <div
                        className="font-medium cursor-pointer hover:underline"
                        onClick={() => navigate(getDetailPath(contact.id))}
                      >
                        {getContactDisplayName(contact) || "—"}
                      </div>
                    </TableCell>

                    <TableCell>{contact.company || "—"}</TableCell>

                    <TableCell>
                      {contact.phones?.length ? (
                        <div className="flex flex-col gap-1">
                          {contact.phones.slice(0, 2).map((p) => (
                            <div key={p.id} className="flex items-center gap-2 text-sm">
                              <Phone className="h-3 w-3 text-muted-foreground" />
                              <span className="text-muted-foreground">{p.phoneType}:</span>
                              <span className="font-medium">{p.phoneNumberE164 || p.phoneNumber}</span>
                            </div>
                          ))}
                          {contact.phones.length > 2 && (
                            <div className="text-xs text-muted-foreground">+{contact.phones.length - 2} more</div>
                          )}
                        </div>
                      ) : (
                        <span className="text-muted-foreground text-sm">—</span>
                      )}
                    </TableCell>

                    <TableCell>
                      {listNames.length ? (
                        <div className="flex flex-wrap gap-1 max-w-xs">
                          {listNames.slice(0, 2).map((name) => (
                            <Badge key={name} variant="secondary" className="truncate max-w-40">
                              {name}
                            </Badge>
                          ))}
                          {listNames.length > 2 && <Badge variant="outline">+{listNames.length - 2}</Badge>}
                        </div>
                      ) : (
                        <span className="text-muted-foreground text-sm">—</span>
                      )}
                    </TableCell>

                    <TableCell>
                      {contact.contactTags?.length ? (
                        <div className="flex flex-wrap gap-1 max-w-xs">
                          {contact.contactTags.slice(0, 3).map((contactTag) => (
                            <Badge
                              key={contactTag.id}
                              className="truncate max-w-30 text-white text-[10px]"
                              style={{ backgroundColor: contactTag.tag?.color ?? "#6B7280" }}
                            >
                              {contactTag.tag?.name}
                            </Badge>
                          ))}
                          {contact.contactTags.length > 3 && (
                            <Badge variant="outline">+{contact.contactTags.length - 3}</Badge>
                          )}
                        </div>
                      ) : (
                        <span className="text-muted-foreground text-sm">—</span>
                      )}
                    </TableCell>

                    <TableCell>
                      {contact.activeDeal?.stage && <DealStageChip stage={contact.activeDeal.stage} />}
                      {!contact.activeDeal && isLeadsPage && !showArchived && (
                        <Button
                          variant="outline"
                          size="sm"
                          disabled={promotingContactId === contact.id}
                          onClick={() => void handlePromote(contact)}
                          title="Start a deal for this lead in the default stage"
                        >
                          Promote
                        </Button>
                      )}
                    </TableCell>

                    <TableCell>{contact.type || "—"}</TableCell>

                    <TableCell>{contact.createdAt ? new Date(contact.createdAt).toLocaleDateString() : "—"}</TableCell>

                    <TableCell className="text-right">
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
                          <Button variant="ghost" size="sm">
                            <MoreVertical className="h-4 w-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onClick={() => navigate(getDetailPath(contact.id))}>
                            <EyeIcon className="mr-2 h-4 w-4" />
                            View
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => navigate(`/contacts/${contact.id}/edit`)}>
                            <Pencil className="mr-2 h-4 w-4" />
                            Edit
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => setContactToTrace(contact)}>
                            <ScanSearch className="mr-2 h-4 w-4" />
                            Skip Trace
                          </DropdownMenuItem>
                          <DropdownMenuSeparator />
                          {showArchived ? (
                            <>
                              <DropdownMenuItem onClick={() => void handleRestore(contact)}>
                                <ArchiveRestore className="mr-2 h-4 w-4" />
                                Restore
                              </DropdownMenuItem>
                              <DropdownMenuItem onClick={() => setContactToPurge(contact)} className="text-destructive">
                                <Trash2 className="mr-2 h-4 w-4" />
                                Delete permanently
                              </DropdownMenuItem>
                            </>
                          ) : (
                            <DropdownMenuItem onClick={() => setContactToDelete(contact)} className="text-destructive">
                              <Trash2 className="mr-2 h-4 w-4" />
                              Archive
                            </DropdownMenuItem>
                          )}
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                  </TableRow>
                );
              })}
              {contacts.length === 0 && (
                <TableRow>
                  <TableCell colSpan={10} className="py-12">
                    <div className="flex flex-col items-center gap-3 text-center">
                      {showArchived ? (
                        <p className="text-muted-foreground">No archived {heading.toLowerCase()}.</p>
                      ) : viewNarrowed ? (
                        <>
                          <p className="text-muted-foreground">No {heading.toLowerCase()} match this view.</p>
                          {filtersActive && (
                            <Button variant="outline" size="sm" onClick={clearFilters}>
                              <X className="mr-2 h-4 w-4" />
                              Clear filters
                            </Button>
                          )}
                        </>
                      ) : (
                        <>
                          <div className="space-y-1">
                            <p className="font-semibold">No {heading.toLowerCase()} yet</p>
                            <p className="text-sm text-muted-foreground">
                              Import a list or create your first {singularName}.
                            </p>
                          </div>
                          <div className="flex gap-2">
                            <Button variant="outline" onClick={() => setImportOpen(true)}>
                              <Upload className="mr-2 h-4 w-4" />
                              Import
                            </Button>
                            <Button onClick={() => navigate(createPath)}>
                              <Plus className="mr-2 h-4 w-4" />
                              {createLabel}
                            </Button>
                          </div>
                        </>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>

          {totalElements > 0 && (
            <div className="flex items-center justify-between pt-2">
              <div className="flex items-center gap-3">
                <span className="text-sm text-muted-foreground">
                  {totalElements} {totalElements === 1 ? "result" : "results"}
                  {totalPages > 1 && ` · Page ${page + 1} of ${totalPages}`}
                </span>
                <Select
                  value={String(size)}
                  onValueChange={(v) => {
                    setSize(Number(v));
                    setPage(0);
                  }}
                >
                  <SelectTrigger className="w-25">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="10">10</SelectItem>
                    <SelectItem value="20">20</SelectItem>
                    <SelectItem value="50">50</SelectItem>
                    <SelectItem value="100">100</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              {totalPages > 1 && (
                <div className="flex items-center gap-2">
                  {isFetching && <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />}
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setPage((page) => Math.max(0, page - 1))}
                    disabled={isFetching || page === 0 || contacts.length === 0}
                  >
                    <ChevronLeft className="h-4 w-4 mr-1" />
                    Previous
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setPage((page) => page + 1)}
                    disabled={isFetching || isLast || contacts.length === 0}
                  >
                    Next
                    <ChevronRight className="h-4 w-4 ml-1" />
                  </Button>
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {contactToTrace?.id && (
        <SkipTraceDialog
          scope={SkipTraceScope.CONTACT}
          targetId={contactToTrace.id}
          targetName={getContactDisplayName(contactToTrace) ?? undefined}
          open={!!contactToTrace}
          onOpenChange={(open) => {
            if (!open) {
              setContactToTrace(null);
            }
          }}
        />
      )}

      {traceSelectionOpen && (
        <SkipTraceDialog
          scope={SkipTraceScope.SELECTION}
          contactIds={Array.from(selectedIds)}
          targetName={`${selectedCount} selected`}
          open={traceSelectionOpen}
          onOpenChange={setTraceSelectionOpen}
        />
      )}

      <AlertDialog open={!!contactToDelete} onOpenChange={() => setContactToDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Archive contact</AlertDialogTitle>
            <AlertDialogDescription>
              <span className="font-semibold">
                {[contactToDelete?.firstName, contactToDelete?.lastName].filter(Boolean).join(" ") || "This contact"}
              </span>{" "}
              leaves your contacts, lists and inbox. Calls, texts, deals and tasks are kept, and the contact comes back on its own if they text or call again.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-destructive">
              Archive
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={bulkDeleteOpen} onOpenChange={setBulkDeleteOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Archive selected contacts</AlertDialogTitle>
            <AlertDialogDescription>
              You are about to archive <span className="font-semibold">{selectedCount}</span> contact
              {selectedCount === 1 ? "" : "s"}. They leave your contacts, lists and inbox. Their calls, texts, deals and tasks are kept, and each comes back on its own if they text or call again.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleBulkDelete} className="bg-destructive">
              Archive
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={!!contactToPurge} onOpenChange={() => setContactToPurge(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete contact permanently</AlertDialogTitle>
            <AlertDialogDescription>
              <span className="font-semibold">
                {[contactToPurge?.firstName, contactToPurge?.lastName].filter(Boolean).join(" ") || "This contact"}
              </span>{" "}
              is removed entirely when there are no calls, texts, deals or tasks. With history, the calls and texts stay under the phone number only and every other detail is erased. This cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handlePurge} className="bg-destructive">
              Delete permanently
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <ImportWizard
        open={importOpen}
        onClose={handleCloseImport}
        entityType="CONTACT"
        contactType={lockedType === ContactType.LEAD ? "LEAD" : "CONTACT"}
      />

      <ExportDialog
        open={exportOpen}
        onClose={() => setExportOpen(false)}
        title={`Export ${exportCount.toLocaleString()} ${heading.toLowerCase()}`}
        description="The file is a CSV that opens in Excel or Google Sheets."
        request={exportRequest}
        allowColumnChoice
      />
    </div>
  );
}
